import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { budgetAccountCodesApi } from '../budgetCatalogsApi';
import type { BudgetAccountCodeListItem } from '../budgetCatalogTypes';
import { sampleBudgetAccountCode } from '../utils/sampleBudgetCatalogs';

/**
 * Create or edit one expenditure account.
 *
 * Only the CODE is held once among the live rows. The account number and the description
 * carry no constraint, deliberately: an organization that numbers two accounts alike has a
 * bookkeeping question rather than a constraint violation, and two accounts may legitimately
 * be described in the same words.
 */
export function BudgetAccountCodeModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  editing: BudgetAccountCodeListItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [code, setCode] = useState(editing?.code ?? '');
  const [acctNo, setAcctNo] = useState(editing?.acctNo ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const valid = code.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      code: code.trim(),
      acctNo: acctNo.trim() || null,
      description: description.trim() || null,
    };

    try {
      if (editing) await budgetAccountCodesApi.update(editing.id, request);
      else await budgetAccountCodesApi.create(request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save the account code.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'budget-account-code' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit account code' : 'New account code'}
      size="lg"
      id="budget-account-code-modal"
      autogenerate={{
        onGenerate: () => {
          const sample = sampleBudgetAccountCode();
          setCode(sample.code);
          setAcctNo(sample.acctNo);
          setDescription(sample.description);
        },
        disabled: saving,
      }}
      confirm={{
        id: 'budget-account-code-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="budget-account-code-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            id="budget-account-code-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="budget-account-code-code"
            label="Code"
            required
            description="The UACS expenditure code, e.g. 5-02-03-010. Held once among the live rows — this is what identifies the account."
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={50}
            disabled={saving}
            error={error?.fieldError('code')}
          />

          <TextField
            id="budget-account-code-acct-no"
            label="Account no."
            description="The account number as the chart prints it, e.g. 1001. Deliberately not unique."
            value={acctNo}
            onChange={(e) => setAcctNo(e.target.value)}
            maxLength={50}
            disabled={saving}
          />
        </div>

        <TextField
          id="budget-account-code-description"
          label="Description"
          description="What the account covers, e.g. Office Supplies Expenses. Shown beside the code wherever a line is charged to it."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
